import { ClockCircleOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, Select } from "antd"
import dayjs from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { unwrapEnvelope } from "../../../../utils/api/unwrap-envelope.ts"
import { showResponseMessage } from "../../../../utils/handlers/show-response-message.ts"
import {
  type SmsSendWindowSettings,
  getSmsSendWindowSettings,
  updateSmsSendWindowSettings
} from "../../api/sms-send-window-settings.services.ts"
import { SettingsCard } from "../common/SettingsCard.tsx"

interface FormValues {
  sms_window_start: string
  sms_window_end: string
}

const STEP_MINUTES = 15
const DRAFT_KEY = "settings.smsSendWindow.draft"

const minutesOf = (hhmm: string) => {
  const [hours, minutes] = hhmm.split(":").map(Number)
  return hours * 60 + minutes
}

const hhmmOf = (total: number) =>
  `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`

export const displayTime = (hhmm: string) =>
  dayjs(`2000-01-01T${hhmm}`).format("h:mm A")

const timesBetween = (earliest: string, latest: string) => {
  const times: string[] = []
  for (
    let at = minutesOf(earliest);
    at <= minutesOf(latest);
    at += STEP_MINUTES
  ) {
    times.push(hhmmOf(at))
  }
  return times
}

const draftKey = (owner: string) => `${DRAFT_KEY}.${owner}`

const readDraft = (owner?: string): FormValues | null => {
  if (!owner) return null
  try {
    const draft = JSON.parse(
      window.sessionStorage.getItem(draftKey(owner)) ?? "null"
    )
    return draft?.sms_window_start && draft?.sms_window_end ? draft : null
  } catch {
    return null
  }
}

const writeDraft = (owner: string | undefined, values: FormValues | null) => {
  if (!owner) return
  try {
    if (values)
      window.sessionStorage.setItem(draftKey(owner), JSON.stringify(values))
    else window.sessionStorage.removeItem(draftKey(owner))
  } catch {
    // Storage can be unavailable (private window); the draft is a convenience.
  }
}

const reasonsOf = (data: unknown): string[] | null => {
  const body = data as
    | {
        sms_window?: string[] | string
        details?: { field_errors?: { sms_window?: string[] | string } }
      }
    | null
    | undefined
  const reason = body?.details?.field_errors?.sms_window ?? body?.sms_window
  if (!reason) return null
  return Array.isArray(reason) ? reason : [reason]
}

const savedValues = (settings: SmsSendWindowSettings): FormValues => ({
  sms_window_start: settings.sms_window_start,
  sms_window_end: settings.sms_window_end
})

const sameWindow = (a: FormValues, b: FormValues) =>
  a.sms_window_start === b.sms_window_start &&
  a.sms_window_end === b.sms_window_end

export function SmsSendWindowCard({
  readonly = false,
  draftOwner
}: {
  readonly?: boolean
  draftOwner?: string
}) {
  const { t } = useTranslation()
  const [settings, setSettings] = useState<SmsSendWindowSettings | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadState, setLoadState] = useState<"loading" | "loaded" | "error">(
    "loading"
  )
  const [loadAttempt, setLoadAttempt] = useState(0)
  const [form] = Form.useForm<FormValues>()
  const [dirty, setDirty] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [draftRestored, setDraftRestored] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoadState("loading")

    void getSmsSendWindowSettings().then((response) => {
      if (cancelled) return
      if (response?.ok && response.data) {
        setSettings(response.data)
        setLoadState("loaded")
      } else {
        setLoadState("error")
      }
    })

    return () => {
      cancelled = true
    }
  }, [loadAttempt])

  useEffect(() => {
    if (!settings || dirty) return
    const draft = readonly ? null : readDraft(draftOwner)
    if (draft && !sameWindow(draft, savedValues(settings))) {
      form.setFieldsValue(draft)
      setDirty(true)
      setDraftRestored(true)
      return
    }
    form.setFieldsValue(savedValues(settings))
  }, [form, settings, dirty, readonly, draftOwner])

  const options = useMemo(
    () =>
      settings
        ? timesBetween(settings.earliest, settings.latest).map((value) => ({
            value,
            label: displayTime(value)
          }))
        : [],
    [settings]
  )

  const discardDraft = () => {
    writeDraft(draftOwner, null)
    setDraftRestored(false)
    setDirty(false)
  }

  const handleFinish = async (values: FormValues) => {
    setSaving(true)
    setSaveFailed(false)
    try {
      const response = await updateSmsSendWindowSettings(values)
      if (response?.ok && response.data) {
        setSettings(unwrapEnvelope<SmsSendWindowSettings>(response.data))
        setDirty(false)
        setDraftRestored(false)
        writeDraft(draftOwner, null)
        showResponseMessage(
          response,
          t("settingsConfiguration.smsSendWindow.saved")
        )
        return
      }
      const reasons = reasonsOf(response?.data)
      if (reasons) {
        form.setFields([{ name: "sms_window_end", errors: reasons }])
      } else {
        setSaveFailed(true)
      }
    } finally {
      setSaving(false)
    }
  }

  const bounds = settings
    ? {
        earliest: displayTime(settings.earliest),
        latest: displayTime(settings.latest)
      }
    : undefined

  return (
    <SettingsCard
      iconWrapperClassName="bg-blue-100"
      icon={<ClockCircleOutlined className="text-blue-600" />}
      title={t("settingsConfiguration.smsSendWindow.title")}
      subtitle={t("settingsConfiguration.smsSendWindow.subtitle")}
    >
      {loadState === "error" && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.smsSendWindow.loadError")}
          action={
            <Button size="small" onClick={() => setLoadAttempt((n) => n + 1)}>
              {t("settingsConfiguration.smsSendWindow.retry")}
            </Button>
          }
        />
      )}
      {saveFailed && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.smsSendWindow.saveFailed")}
        />
      )}
      {draftRestored && (
        <Alert
          type="info"
          showIcon
          className="mb-4"
          message={t("settingsConfiguration.smsSendWindow.draftRestored")}
          action={
            <Button size="small" onClick={discardDraft}>
              {t("settingsConfiguration.smsSendWindow.discard")}
            </Button>
          }
        />
      )}
      {readonly && loadState === "loaded" && (
        <p className="mb-4 text-sm text-gray-500">
          {t("settingsConfiguration.smsSendWindow.viewOnly")}
        </p>
      )}
      <Form
        form={form}
        layout="vertical"
        className="with-default-error"
        disabled={readonly || saving || loadState !== "loaded"}
        onValuesChange={(_changed: Partial<FormValues>, all: FormValues) => {
          setDirty(true)
          setSaveFailed(false)
          writeDraft(draftOwner, all)
        }}
        onFinish={handleFinish}
      >
        <Flex gap={16} wrap>
          <Form.Item
            name="sms_window_start"
            label={t("settingsConfiguration.smsSendWindow.start")}
            rules={[
              {
                required: true,
                message: t("settingsConfiguration.smsSendWindow.required")
              }
            ]}
          >
            <Select
              className="w-40"
              options={options}
              virtual={false}
              showSearch
              optionFilterProp="label"
              aria-label={t("settingsConfiguration.smsSendWindow.start")}
            />
          </Form.Item>
          <Form.Item
            name="sms_window_end"
            label={t("settingsConfiguration.smsSendWindow.end")}
            dependencies={["sms_window_start"]}
            rules={[
              {
                required: true,
                message: t("settingsConfiguration.smsSendWindow.required")
              },
              ({ getFieldValue }) => ({
                validator: (_rule, end?: string) => {
                  const start = getFieldValue("sms_window_start") as
                    | string
                    | undefined
                  if (!start || !end || minutesOf(end) > minutesOf(start)) {
                    return Promise.resolve()
                  }
                  return Promise.reject(
                    new Error(
                      t("settingsConfiguration.smsSendWindow.endBeforeStart")
                    )
                  )
                }
              })
            ]}
          >
            <Select
              className="w-40"
              options={options}
              virtual={false}
              showSearch
              optionFilterProp="label"
              aria-label={t("settingsConfiguration.smsSendWindow.end")}
            />
          </Form.Item>
        </Flex>
        <p className="mb-2 text-sm text-gray-500">
          {bounds
            ? t("settingsConfiguration.smsSendWindow.hint", bounds)
            : null}
        </p>
        <p className="mb-4 text-sm text-gray-500">
          {t("settingsConfiguration.smsSendWindow.alwaysSent")}
        </p>

        <Flex justify="end">
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={readonly || !dirty || loadState !== "loaded"}
          >
            {t("settingsConfiguration.smsSendWindow.save")}
          </Button>
        </Flex>
      </Form>
    </SettingsCard>
  )
}
